import { PermissionsRiskLevelSummaryFieldsFragment } from '@api/gql/graphql';
import { LOW_RISK_THRESHOLD, MEDIUM_RISK_THRESHOLD, RiskScore } from '@common/components/RiskScore';
import { calculateRiskScore, RISK_LEVEL_ENUM } from '@common/utils/calculateRiskScore';
import { Stack, Typography } from '@mui/material';

export function RiskScoreSummary({
  permissionsRiskLevelSummary,
  resourceRiskLevel,
}: {
  permissionsRiskLevelSummary: PermissionsRiskLevelSummaryFieldsFragment;
  resourceRiskLevel: string;
}) {
  const { riskScore: permissionLowRiskScore } = calculateRiskScore(resourceRiskLevel, RISK_LEVEL_ENUM.low);
  const { riskScore: permissionMediumRiskScore } = calculateRiskScore(resourceRiskLevel, RISK_LEVEL_ENUM.medium);
  const { riskScore: permissionHighRiskScore } = calculateRiskScore(resourceRiskLevel, RISK_LEVEL_ENUM.high);

  const riskCounts = getRiskLevelCounts(
    permissionsRiskLevelSummary,
    permissionLowRiskScore,
    permissionMediumRiskScore,
    permissionHighRiskScore,
  );

  return (
    <Stack direction="row" spacing={1}>
      {renderRiskScoreStack(RISK_LEVEL_ENUM.high, RISK_LEVEL_ENUM.high, riskCounts.high)}
      {renderRiskScoreStack(RISK_LEVEL_ENUM.low, RISK_LEVEL_ENUM.medium, riskCounts.medium)}
      {renderRiskScoreStack(RISK_LEVEL_ENUM.low, RISK_LEVEL_ENUM.low, riskCounts.low)}
    </Stack>
  );
}

// This function calculates the counts of each risk level.
function getRiskLevelCounts(
  permissionsRiskLevelSummary: PermissionsRiskLevelSummaryFieldsFragment,
  permissionLowRiskScore: number,
  permissionMediumRiskScore: number,
  permissionHighRiskScore: number,
) {
  const riskCounts = {
    low: 0,
    medium: 0,
    high: 0,
  };

  addToRiskScore(riskCounts, permissionLowRiskScore, permissionsRiskLevelSummary.lowLevelCount);
  addToRiskScore(riskCounts, permissionMediumRiskScore, permissionsRiskLevelSummary.mediumLevelCount);
  addToRiskScore(riskCounts, permissionHighRiskScore, permissionsRiskLevelSummary.highLevelCount);

  return riskCounts;
}

// This function adds a value to the appropriate risk count based on the risk score.
function addToRiskScore(riskCounts: { low: number; medium: number; high: number }, riskScore: number, value: number) {
  if (riskScore < LOW_RISK_THRESHOLD) {
    riskCounts.low += value;
  } else if (riskScore < MEDIUM_RISK_THRESHOLD) {
    riskCounts.medium += value;
  } else {
    riskCounts.high += value;
  }
}

// This function renders a stack with a risk score and count.
function renderRiskScoreStack(resourceRiskLevel: string, permissionRiskLevel: string, count: number) {
  return (
    <Stack direction="row" spacing={0.5}>
      <RiskScore
        resourceRiskLevel={resourceRiskLevel}
        permissionRiskLevel={permissionRiskLevel}
        withRiskScore={false}
      />
      <Typography variant="body2" width={'20px'}>
        {count}
      </Typography>
    </Stack>
  );
}
